// @ts-nocheck
import { useEffect } from "react";
import socket from "../utilities/socket";
import { toast } from "sonner";
import { ROUTES } from "../utilities/constant";

import { Storage, getUser } from "../utilities/setting";
import { getAgentMasking, maskNumber } from "../utilities/cliDisplay";
import { postApiCall } from "../service/httpCall";
import { API_USER_LOGOUT } from "../service/url";

const HEARTBEAT_INTERVAL_MS = 10000;

const SLEEP_GAP_THRESHOLD_MS = 5 * 60 * 1000;

const JOIN_REVALIDATE_THROTTLE_MS = 30_000;

function createHeartbeatWorker() {
  if (typeof Worker === "undefined") {
    return null;
  }

  const workerScript = `
    let heartbeatTimer = null;

    self.onmessage = function(event) {
      const { type, interval } = event.data || {};

      if (type === "START") {
        if (heartbeatTimer) {
          clearInterval(heartbeatTimer);
        }

        heartbeatTimer = setInterval(function() {
          self.postMessage({ type: "HEARTBEAT" });
        }, interval);
      }

      if (type === "STOP" && heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };
  `;

  const workerBlob = new Blob([workerScript], {
    type: "application/javascript",
  });
  const workerUrl = URL.createObjectURL(workerBlob);

  return {
    worker: new Worker(workerUrl),
    workerUrl,
  };
}

function useSocket(agentId) {

  useEffect(() => {
    if (!agentId) return;

    console.log("Initializing socket for:", agentId);
    const handleConnect = () => {
      console.log("Connected:", socket.id);

      socket.emit("join", { agentId });
      window.dispatchEvent(new CustomEvent("cti:notification_refresh", { detail: {} }));
    };

    const handleConnectError = (err) => {
      console.log("Connection error:", err.message);
    };

    const handleForceLogout = async () => {
      console.log("Force logout received");

      Storage.clearAll();

      socket.disconnect();

      window.location.replace(`/${ROUTES.LOGIN}`);
    };

    const handleDisconnect = (reason) => {
      console.log("Socket disconnected:", reason);
    };

    const handleReconnectAttempt = () => {
      console.log("Reconnecting...");
    };

    const handleReconnect = () => {
      console.log("Reconnected");
      socket.emit("join", { agentId });
    };

    const handleReconnectFailed = () => {
      console.log("Reconnect failed");
    };

    const handleBreakExceededAgent = (payload) => {
      const breakTitle = payload?.break?.title || "Break";
      const allowed = payload?.break?.allowed_duration_minutes;
      const message =
        `Your "${breakTitle}" break has exceeded the allowed duration` +
        (allowed ? ` of ${allowed} min.` : ".") +
        " Please end the break.";

      toast.warning(message, {
        autoClose: 8000,
        toastId: `break-exceeded-agent-${payload?.userBreakId || "self"}-${
          payload?.exceeded_at || Date.now()
        }`,
      });
    };

    // "5:30 PM", or "10 Oct, 5:30 PM" when it isn't today (IST, like the server).
    const formatScheduledAt = (payload) => {
      const d = new Date(payload?.scheduled_at || "");
      if (Number.isNaN(d.getTime())) {
        return String(payload?.follow_up_time || "").slice(0, 5) || "the scheduled time";
      }
      const tz = { timeZone: "Asia/Kolkata" };
      const time = d.toLocaleTimeString("en-US", { ...tz, hour: "numeric", minute: "2-digit" });
      const day = (x) => x.toLocaleDateString("en-GB", { ...tz, day: "numeric", month: "short" });
      return day(d) === day(new Date()) ? time : `${day(d)}, ${time}`;
    };

    const maskNumbers = getAgentMasking(getUser());
    const displayContact = (rawContact) => {
      if (!rawContact) return "your contact";
      return maskNumbers ? maskNumber(rawContact) : rawContact;
    };

    // One toast per server bucket (15/12/9/6/3/0 min); the id dedupes a re-delivery.
    const handleFollowUpReminder = (payload) => {
      const contact = displayContact(payload?.did_number);
      const remaining = Number(payload?.remaining_minutes) || 0;
      const message = remaining > 0
        ? `Reminder: your follow-up with ${contact} is in ${remaining} min (at ${formatScheduledAt(payload)}).`
        : `It's time for your follow-up with ${contact}.`;

      toast.info(message, {
        id: `follow-up-${payload?.followUpId || "soon"}-${payload?.bucket ?? remaining}`,
        duration: 8000,
      });
    };

    // Sent twice by the server (3 and 6 min overdue); each send gets its own toast.
    const handleFollowUpMissedAgent = (payload) => {
      const contact = displayContact(payload?.did_number);
      const message =
        `You missed your follow-up with ${contact} scheduled for ${formatScheduledAt(payload)}. ` +
        "Please call them back when you can.";

      toast.warning(message, {
        id: `follow-up-missed-${payload?.followUpId || "soon"}-${payload?.bucket ?? Date.now()}`,
        duration: 10000,
      });
    };

    const handleNotificationRefresh = (payload) => {
      window.dispatchEvent(
        new CustomEvent("cti:notification_refresh", { detail: payload || {} })
      );
    };

    const handleAnnouncementRefresh = (payload) => {
      window.dispatchEvent(
        new CustomEvent("cti:announcement_refresh", { detail: payload || {} })
      );
    };

    // connect event
    socket.on("connect", handleConnect);

    // error handling
    socket.on("connect_error", handleConnectError);

    // force logout
    socket.on("force_logout", handleForceLogout);

    // agent-side break-exceeded toast (recurring every ~1 min from server)
    socket.on("break_exceeded_agent", handleBreakExceededAgent);

    socket.on("follow_up_reminder", handleFollowUpReminder);
    socket.on("follow_up_missed_agent", handleFollowUpMissedAgent);

    socket.on("notification_refresh", handleNotificationRefresh);
    socket.on("announcement_refresh", handleAnnouncementRefresh);

    // disconnect log
    socket.on("disconnect", handleDisconnect);

    if (
      socket.connected &&
      socket.__agentId &&
      String(socket.__agentId) !== String(agentId)
    ) {
      console.log("Stale socket from previous session — closing:", socket.id);
      socket.disconnect();
    }
    socket.__agentId = String(agentId);

    // connect socket (only if not already)
    if (!socket.connected) {
      socket.connect();
    } else {
      socket.emit("join", { agentId });
    }

    socket.on("reconnect_attempt", handleReconnectAttempt);

    socket.on("reconnect", handleReconnect);

    socket.on("reconnect_failed", handleReconnectFailed);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("force_logout", handleForceLogout);
      socket.off("break_exceeded_agent", handleBreakExceededAgent);
      socket.off("follow_up_reminder", handleFollowUpReminder);
      socket.off("follow_up_missed_agent", handleFollowUpMissedAgent);
      socket.off("notification_refresh", handleNotificationRefresh);
      socket.off("announcement_refresh", handleAnnouncementRefresh);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("reconnect_attempt", handleReconnectAttempt);
      socket.off("reconnect", handleReconnect);
      socket.off("reconnect_failed", handleReconnectFailed);
    };
  }, [agentId]);

  useEffect(() => {
    if (!agentId) return;

    let intervalId = null;
    let heartbeatWorker = null;
    let heartbeatWorkerUrl = null;
    let lastTickAt = Date.now();
    let sleepLogoutFired = false;
    let lastJoinValidationAt = 0;

    const detectSuspendGap = () => {
      const now = Date.now();
      const gap = now - lastTickAt;
      lastTickAt = now;
      return gap > SLEEP_GAP_THRESHOLD_MS;
    };

    const handleSleepLogout = async () => {
      if (sleepLogoutFired) return;
      sleepLogoutFired = true;

      console.log("System suspend detected — logging agent out");
      try {
        const user = getUser();
        if (user?.id) {
          await postApiCall(`${API_USER_LOGOUT}/${user.id}`, {});
        }
      } catch (err) {
        console.log("Sleep logout API failed, falling back to local cleanup:", err);
      }

      try {
        socket.disconnect();
      } catch (e) {
        /* noop */
      }

      Storage.clearAll();
      toast.warning("You were logged out because your system was asleep.");
      window.location.replace(`/${ROUTES.LOGIN}`);
    };

    const sendHeartbeat = () => {
      if (detectSuspendGap()) {
        handleSleepLogout();
        return;
      }

      if (!socket.connected) {
        if (!getUser()?.id) return;
        socket.connect();
        return;
      }

      socket.emit("heartbeat", { agentId });
    };

    const handleWorkerMessage = (event) => {
      if (event.data?.type === "HEARTBEAT") {
        sendHeartbeat();
      }
    };

    const handleVisibilityResume = () => {
      if (detectSuspendGap()) {
        handleSleepLogout();
        return;
      }

      sendHeartbeat();
      const now = Date.now();
      if (socket.connected && now - lastJoinValidationAt > JOIN_REVALIDATE_THROTTLE_MS) {
        lastJoinValidationAt = now;
        socket.emit("join", { agentId });
      }
    };

    sendHeartbeat();

    const workerData = createHeartbeatWorker();

    if (workerData) {
      heartbeatWorker = workerData.worker;
      heartbeatWorkerUrl = workerData.workerUrl;
      heartbeatWorker.addEventListener("message", handleWorkerMessage);
      heartbeatWorker.postMessage({
        type: "START",
        interval: HEARTBEAT_INTERVAL_MS,
      });
    } else {
      intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    }

    window.addEventListener("focus", handleVisibilityResume);
    window.addEventListener("online", handleVisibilityResume);
    window.addEventListener("pageshow", handleVisibilityResume);
    document.addEventListener("visibilitychange", handleVisibilityResume);

    return () => {
      window.removeEventListener("focus", handleVisibilityResume);
      window.removeEventListener("online", handleVisibilityResume);
      window.removeEventListener("pageshow", handleVisibilityResume);
      document.removeEventListener("visibilitychange", handleVisibilityResume);

      if (intervalId) {
        clearInterval(intervalId);
      }

      if (heartbeatWorker) {
        heartbeatWorker.postMessage({ type: "STOP" });
        heartbeatWorker.removeEventListener("message", handleWorkerMessage);
        heartbeatWorker.terminate();
      }

      if (heartbeatWorkerUrl) {
        URL.revokeObjectURL(heartbeatWorkerUrl);
      }
    };
  }, [agentId]);
}

export default useSocket;